import React from 'react'
import { motion } from 'framer-motion'
import { BookOpen, Sparkles, Heart, ArrowLeft, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ParticleBackground from '../components/ParticleBackground'

const KO_FI_URL = 'https://ko-fi.com/migspsalm91studio'

const About: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative">
      <ParticleBackground />
      
      <div className="relative z-10 container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-bible-gold hover:text-yellow-300 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-bible-gold mb-4">
              About Bible Study Presentations
            </h1>
            <p className="text-xl text-gray-300">
              Interactive Biblical Studies
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-slate-700 space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-bible-gold mb-4 flex items-center gap-2">
                <BookOpen size={28} />
                Our Mission
              </h2>
              <p className="text-gray-300 leading-relaxed text-lg">
                Bible Study Presentations is designed to make studying God's Word more accessible, 
                engaging, and interactive. Our platform provides tools to read, study, and present 
                Bible passages in a modern, user-friendly format.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-bible-gold mb-4 flex items-center gap-2">
                <Sparkles size={28} />
                Features
              </h2>
              <ul className="space-y-3 text-gray-300 text-lg">
                <li className="flex items-start gap-3">
                  <span className="text-bible-gold mt-1">•</span>
                  <span><strong className="text-bible-gold">Complete Bible Reader</strong> - Read all 66 books of the Bible with professional typography and mobile-friendly design</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-bible-gold mt-1">•</span>
                  <span><strong className="text-bible-gold">Slideshow Generator</strong> - Automatically generate Bible study presentations from references or topics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-bible-gold mt-1">•</span>
                  <span><strong className="text-bible-gold">ESV Bible Integration</strong> - Access the English Standard Version (ESV) Bible text through our API integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-bible-gold mt-1">•</span>
                  <span><strong className="text-bible-gold">Interactive Studies</strong> - Explore detailed Bible studies with visual presentations and commentary</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-bible-gold mt-1">•</span>
                  <span><strong className="text-bible-gold">Mobile Responsive</strong> - Accessible on all devices, from phones to large presentation screens</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-bible-gold mb-4 flex items-center gap-2">
                <Heart size={28} />
                Our Purpose
              </h2>
              <p className="text-gray-300 leading-relaxed text-lg">
                We believe that studying Scripture should be engaging, accessible, and transformative. 
                This platform is designed to help individuals, small groups, and churches dive deeper 
                into God's Word through modern technology while maintaining reverence for the sacred text.
              </p>
            </div>

            {/* Support Section */}
            <div className="pt-6 border-t border-slate-700">
              <h2 className="text-2xl font-bold text-bible-gold mb-4">Support This Project</h2>
              <p className="text-gray-300 leading-relaxed text-lg mb-6">
                If our Bible study presentations have been helpful to you, consider supporting the development 
                of more resources and studies. Your support helps us create more interactive Bible study content 
                and improve the platform for everyone.
              </p>
              <motion.a
                href={KO_FI_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-bible-gold/20 hover:bg-bible-gold/30 text-bible-gold rounded-lg transition-all duration-300 font-semibold text-lg border border-bible-gold/50"
              >
                <span>☕</span>
                <span>Support on Ko-fi</span>
                <ExternalLink size={18} />
              </motion.a>
            </div>

            <div className="pt-6 border-t border-slate-700">
              <p className="text-gray-400 text-center">
                "All Scripture is breathed out by God and profitable for teaching, for reproof, 
                for correction, and for training in righteousness" - 2 Timothy 3:16 (ESV)
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default About

